/** A native scroll-snap carousel, enhanced with seamless looping and optional autoplay. */
if (!customElements.get('av-testimonials')) {
  customElements.define('av-testimonials', class extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.viewport = this.querySelector('.av-testimonials__viewport');
      if (!this.viewport) return;
      this.initialized = true;
      this.abortController = new AbortController();
      const options = { signal: this.abortController.signal };
      this.cards = [...this.viewport.querySelectorAll('.av-testimonials__card:not([data-testimonial-clone])')];
      this.dots = [...this.querySelectorAll('[data-testimonial-dot]')];
      this.previous = this.querySelector('[data-testimonial-prev]');
      this.next = this.querySelector('[data-testimonial-next]');
      this.pauseButton = this.querySelector('[data-testimonial-pause]');
      this.status = this.querySelector('[data-testimonial-status]');
      this.activeIndex = 0;
      this.autoplay = this.dataset.autoplay === 'true';
      this.speed = Number.isFinite(Number(this.dataset.speed)) ? Math.max(0, Number(this.dataset.speed)) : 450;
      this.interval = Math.max(2000, Number(this.dataset.interval) || 5000);
      this.motion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.manualPause = false;
      this.hovered = false;
      this.focusWithin = this.contains(document.activeElement);
      this.editorPaused = false;
      this.pointerDown = false;
      this.onScreen = !('IntersectionObserver' in window);
      this.originalSnap = this.viewport.style.scrollSnapType;
      this.originalBehavior = this.viewport.style.scrollBehavior;

      this.previous?.addEventListener('click', () => this.move(-1), options);
      this.next?.addEventListener('click', () => this.move(1), options);
      this.dots.forEach((dot) => dot.addEventListener('click', () => {
        this.goTo(Number(dot.dataset.testimonialDot));
      }, options));
      this.pauseButton?.addEventListener('click', () => {
        this.manualPause = !this.manualPause;
        this.updatePauseButton();
        this.scheduleAutoplay();
      }, options);
      this.addEventListener('keydown', (event) => this.onKeydown(event), options);
      this.addEventListener('pointerenter', (event) => {
        if (event.pointerType !== 'mouse') return;
        this.hovered = true;
        this.scheduleAutoplay();
      }, options);
      this.addEventListener('pointerleave', () => {
        this.hovered = false;
        this.scheduleAutoplay();
      }, options);
      this.addEventListener('focusin', () => {
        this.focusWithin = true;
        this.scheduleAutoplay();
      }, options);
      this.addEventListener('focusout', (event) => {
        this.focusWithin = this.contains(event.relatedTarget);
        this.scheduleAutoplay();
      }, options);
      this.viewport.addEventListener('pointerdown', () => {
        this.pointerDown = true;
        this.interrupt();
      }, { ...options, passive: true });
      const releasePointer = () => {
        if (!this.pointerDown) return;
        this.pointerDown = false;
        this.queueSettle();
      };
      window.addEventListener('pointerup', releasePointer, options);
      window.addEventListener('pointercancel', releasePointer, options);
      this.viewport.addEventListener('wheel', () => {
        this.interrupt();
        this.queueSettle();
      }, { ...options, passive: true });
      this.viewport.addEventListener('scroll', () => this.onScroll(), { ...options, passive: true });
      this.viewport.addEventListener('scrollend', () => this.settle(), options);
      document.addEventListener('visibilitychange', () => this.scheduleAutoplay(), options);
      this.motion.addEventListener('change', () => {
        if (this.motion.matches && this.animating) this.goTo(this.activeIndex, { animate: false, announce: false });
        this.updatePauseButton();
        this.scheduleAutoplay();
      }, options);
      document.addEventListener('shopify:block:select', (event) => {
        const index = this.cards.findIndex((card) => card.dataset.blockId === event.detail?.blockId || card.contains(event.target));
        if (index < 0) return;
        this.editorPaused = true;
        this.goTo(index, { animate: false, announce: false });
      }, options);
      document.addEventListener('shopify:block:deselect', (event) => {
        if (!this.cards.some((card) => card.dataset.blockId === event.detail?.blockId || card.contains(event.target))) return;
        this.editorPaused = false;
        this.scheduleAutoplay();
      }, options);

      if ('IntersectionObserver' in window) {
        this.visibilityObserver = new IntersectionObserver(([entry]) => {
          this.onScreen = entry.isIntersecting;
          this.scheduleAutoplay();
        });
        this.visibilityObserver.observe(this);
      }
      const resize = () => {
        cancelAnimationFrame(this.resizeFrame);
        this.resizeFrame = requestAnimationFrame(() => this.refresh());
      };
      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(resize);
        this.resizeObserver.observe(this.viewport);
      } else window.addEventListener('resize', resize, options);
      this.refresh();
      this.classList.add('is-ready');
      this.updatePauseButton();
    }

    disconnectedCallback() {
      this.abortController?.abort();
      this.resizeObserver?.disconnect();
      this.visibilityObserver?.disconnect();
      this.cancelAnimation();
      clearTimeout(this.autoplayTimer);
      clearTimeout(this.settleTimer);
      cancelAnimationFrame(this.scrollFrame);
      cancelAnimationFrame(this.resizeFrame);
      this.scrollFrame = null;
      this.cloneCount = undefined;
      this.viewport?.querySelectorAll('[data-testimonial-clone]').forEach((clone) => clone.remove());
      this.classList.remove('is-ready');
      this.initialized = false;
    }

    createClone(card) {
      const clone = card.cloneNode(true);
      clone.dataset.testimonialClone = 'true';
      clone.setAttribute('aria-hidden', 'true');
      clone.setAttribute('inert', '');
      [clone, ...clone.querySelectorAll('*')].forEach((element) => {
        element.removeAttribute('id');
        element.removeAttribute('data-block-id');
        element.removeAttribute('data-shopify-editor-block');
      });
      clone.querySelectorAll('a, button, input, select, textarea, [tabindex]').forEach((element) => {
        element.setAttribute('tabindex', '-1');
      });
      return clone;
    }

    refresh() {
      if (!this.isConnected) return;
      this.cancelAnimation();
      clearTimeout(this.settleTimer);
      const styles = getComputedStyle(this.viewport);
      this.rtl = styles.direction === 'rtl';
      this.paddingStart = parseFloat(this.rtl ? styles.paddingRight : styles.paddingLeft) || 0;
      const visible = Math.max(1, Number(styles.getPropertyValue('--av-testimonial-visible')) || 1);
      const cloneCount = this.cards.length > 1 ? Math.min(this.cards.length, Math.ceil(visible) + 1) : 0;
      if (cloneCount !== this.cloneCount) {
        this.viewport.querySelectorAll('[data-testimonial-clone]').forEach((clone) => clone.remove());
        const before = document.createDocumentFragment();
        const after = document.createDocumentFragment();
        this.cards.slice(-cloneCount || this.cards.length).forEach((card) => before.append(this.createClone(card)));
        this.cards.slice(0, cloneCount).forEach((card) => after.append(this.createClone(card)));
        this.viewport.prepend(before);
        this.viewport.append(after);
        this.cloneCount = cloneCount;
      }
      this.slides = [...this.viewport.querySelectorAll('.av-testimonials__card')];
      this.viewportWidth = this.viewport.clientWidth;
      const viewportBounds = this.viewport.getBoundingClientRect();
      this.positions = this.slides.map((slide) => {
        const bounds = slide.getBoundingClientRect();
        return this.viewport.scrollLeft + (this.rtl
          ? bounds.right - viewportBounds.right + this.paddingStart
          : bounds.left - viewportBounds.left - this.paddingStart);
      });
      this.cardWidth = this.cards[0]?.getBoundingClientRect().width || 0;
      this.activeIndex = Math.min(this.activeIndex, Math.max(0, this.cards.length - 1));
      if (this.cards.length) this.jumpTo(this.positions[this.cloneCount + this.activeIndex]);
      [this.previous, this.next, this.pauseButton, ...this.dots].forEach((button) => {
        if (button) button.disabled = this.cards.length < 2;
      });
      this.updateState();
      this.scheduleAutoplay();
    }

    jumpTo(position) {
      this.viewport.style.scrollSnapType = 'none';
      this.viewport.style.scrollBehavior = 'auto';
      this.viewport.scrollLeft = position;
      this.viewport.style.scrollSnapType = this.originalSnap;
      this.viewport.style.scrollBehavior = this.originalBehavior;
    }

    nearestSlide() {
      let nearest = 0;
      let distance = Infinity;
      this.positions.forEach((position, index) => {
        const nextDistance = Math.abs(position - this.viewport.scrollLeft);
        if (nextDistance < distance) {
          nearest = index;
          distance = nextDistance;
        }
      });
      return nearest;
    }

    move(direction, announce = true) {
      this.goTo(this.activeIndex + direction, { direction, announce });
    }

    goTo(index, { animate = true, announce = true, direction = 0 } = {}) {
      if (!this.cards.length || !Number.isFinite(index)) return;
      this.cancelAnimation();
      clearTimeout(this.autoplayTimer);
      clearTimeout(this.settleTimer);
      const nextIndex = ((index % this.cards.length) + this.cards.length) % this.cards.length;
      let destination = this.cloneCount + nextIndex;
      let shortest = Infinity;
      this.slides.forEach((slide, slideIndex) => {
        if (Number(slide.dataset.testimonialIndex) !== nextIndex) return;
        const delta = (this.positions[slideIndex] - this.viewport.scrollLeft) * (this.rtl ? -1 : 1);
        if (direction && delta * direction < 1) return;
        if (Math.abs(delta) < shortest) {
          shortest = Math.abs(delta);
          destination = slideIndex;
        }
      });
      this.activeIndex = nextIndex;
      this.pendingAnnounce = announce;
      this.updateState();
      const target = this.positions[destination];
      const start = this.viewport.scrollLeft;
      const duration = animate && !this.motion.matches ? this.speed : 0;
      if (!duration || Math.abs(target - start) < 1) {
        this.jumpTo(target);
        this.settle();
        return;
      }
      this.animating = true;
      this.viewport.style.scrollSnapType = 'none';
      this.viewport.style.scrollBehavior = 'auto';
      const began = performance.now();
      const frame = (now) => {
        const progress = Math.min(1, (now - began) / duration);
        const eased = 1 - Math.pow(1 - progress, 3);
        this.viewport.scrollLeft = start + (target - start) * eased;
        this.updateState();
        if (progress < 1) this.animationFrame = requestAnimationFrame(frame);
        else {
          this.cancelAnimation();
          this.jumpTo(target);
          this.settle();
        }
      };
      this.animationFrame = requestAnimationFrame(frame);
    }

    cancelAnimation() {
      cancelAnimationFrame(this.animationFrame);
      this.animating = false;
      if (!this.viewport) return;
      this.viewport.style.scrollSnapType = this.originalSnap || '';
      this.viewport.style.scrollBehavior = this.originalBehavior || '';
    }

    interrupt() {
      this.cancelAnimation();
      clearTimeout(this.autoplayTimer);
      this.pendingAnnounce = true;
    }

    onScroll() {
      clearTimeout(this.autoplayTimer);
      if (!this.scrollFrame) {
        this.scrollFrame = requestAnimationFrame(() => {
          this.scrollFrame = null;
          // Browsers may resnap before ResizeObserver runs; old positions must not change the active review.
          if (this.viewport.clientWidth !== this.viewportWidth) {
            this.refresh();
            return;
          }
          if (!this.animating && this.slides.length) {
            this.activeIndex = Number(this.slides[this.nearestSlide()].dataset.testimonialIndex);
          }
          this.updateState();
        });
      }
      this.queueSettle();
    }

    queueSettle() {
      clearTimeout(this.settleTimer);
      this.settleTimer = setTimeout(() => this.settle(), 160);
    }

    settle() {
      if (this.animating || this.pointerDown || !this.slides?.length) return;
      if (this.viewport.clientWidth !== this.viewportWidth) {
        this.refresh();
        return;
      }
      clearTimeout(this.settleTimer);
      const nearest = this.nearestSlide();
      this.activeIndex = Number(this.slides[nearest].dataset.testimonialIndex);
      if (this.slides[nearest].hasAttribute('data-testimonial-clone')) {
        this.jumpTo(this.positions[this.cloneCount + this.activeIndex]);
      }
      this.updateState();
      if (this.status && this.pendingAnnounce) {
        this.status.textContent = `Testimonial ${this.activeIndex + 1} of ${this.cards.length}`;
      }
      this.pendingAnnounce = false;
      this.scheduleAutoplay();
    }

    updateState() {
      this.slides?.forEach((slide, index) => {
        const offset = (this.positions[index] - this.viewport.scrollLeft) * (this.rtl ? -1 : 1);
        const start = this.paddingStart + offset;
        slide.classList.toggle('is-visible', start >= -1 && start + this.cardWidth <= this.viewport.clientWidth + 1);
        slide.classList.toggle('is-active', Number(slide.dataset.testimonialIndex) === this.activeIndex);
      });
      this.dots.forEach((dot) => {
        const active = Number(dot.dataset.testimonialDot) === this.activeIndex;
        dot.classList.toggle('is-active', active);
        if (active) dot.setAttribute('aria-current', 'true');
        else dot.removeAttribute('aria-current');
      });
    }

    onKeydown(event) {
      if (event.altKey || event.ctrlKey || event.metaKey || event.target.closest('input, select, textarea, [contenteditable="true"]')) return;
      const direction = event.key === 'ArrowRight' ? 1 : event.key === 'ArrowLeft' ? -1 : 0;
      if (direction) {
        event.preventDefault();
        this.move(direction * (this.rtl ? -1 : 1));
      } else if (event.key === 'Home' || event.key === 'End') {
        event.preventDefault();
        this.goTo(event.key === 'Home' ? 0 : this.cards.length - 1);
      }
    }

    updatePauseButton() {
      if (!this.pauseButton) return;
      const paused = this.manualPause || this.motion.matches;
      const label = paused
        ? this.pauseButton.dataset.labelPlay || 'Play slideshow'
        : this.pauseButton.dataset.labelPause || 'Pause slideshow';
      this.pauseButton.textContent = label;
      this.pauseButton.setAttribute('aria-label', label);
      this.pauseButton.hidden = !this.autoplay || this.cards.length < 2 || this.motion.matches;
    }

    scheduleAutoplay() {
      clearTimeout(this.autoplayTimer);
      if (!this.autoplay || this.cards.length < 2 || this.manualPause || this.motion.matches || this.hovered ||
        this.focusWithin || this.editorPaused || this.pointerDown || this.animating || !this.onScreen || document.hidden) return;
      this.autoplayTimer = setTimeout(() => this.move(1, false), this.interval);
    }
  });
}
